import type { Connector, ConnectorAuth, ConnectorAuthUpdate, ConnectorCreate, ConnectorUpdate } from "~/shared/types";
import { makeDuid } from "~/utils/common";

import type { PiniaActionAdaptor, PiniaGetterAdaptor } from "../shared";
import type { DataStore } from ".";

export type State = {
  _duidsToConnectors: Map<string, Connector>;
  _duidsToConnectorAuths: Map<string, ConnectorAuth>;
};

type ConnectorCreatePartial = Pick<Partial<ConnectorCreate>, "enabled" | "clientId" | "clientSecret">;

export const state: State = {
  _duidsToConnectors: new Map(),
  _duidsToConnectorAuths: new Map(),
};

export type Getters = {
  connectorList: Connector[];
  connectorAuthList: ConnectorAuth[];
  getConnectorByDuid: (duid: string) => Connector | undefined;
  getConnectorAuthByDuid: (duid: string) => ConnectorAuth | undefined;
  getConnectorAuthsForConnector: (connectorDuid: string) => ConnectorAuth[];
};

export type Actions = {
  createConnector: (
    name: string,
    url: string,
    partialConnector?: ConnectorCreatePartial,
    options?: { awaitBackend?: boolean }
  ) => Promise<Connector | undefined>;
  updateConnector: (
    update: ConnectorUpdate,
    options?: { awaitBackend?: boolean; noBackend?: boolean }
  ) => Promise<Connector | undefined>;
  updateConnectorAuth: (
    update: ConnectorAuthUpdate,
    options?: { awaitBackend?: boolean; noBackend?: boolean }
  ) => Promise<ConnectorAuth | undefined>;
  deleteConnector: (connector: Connector, options?: { awaitBackend?: boolean }) => Promise<void>;
  deleteConnectorAuth: (connectorAuth: ConnectorAuth, options?: { awaitBackend?: boolean }) => Promise<void>;
  $setConnectors: (connectors: Connector[]) => void;
  $setConnectorAuths: (connectorAuths: ConnectorAuth[]) => void;
  $createOrUpdateConnector: (connector: Connector) => void;
  $deleteConnector: (connector: Connector) => void;
  $createOrUpdateConnectorAuth: (connectorAuth: ConnectorAuth) => void;
  $deleteConnectorAuth: (connectorAuth: ConnectorAuth) => void;
};

const getters: PiniaGetterAdaptor<Getters, DataStore> = {
  connectorList() {
    return [...this._duidsToConnectors.values()];
  },
  connectorAuthList() {
    return [...this._duidsToConnectorAuths.values()];
  },
  getConnectorByDuid() {
    return (duid) => this._duidsToConnectors.get(duid);
  },
  getConnectorAuthByDuid() {
    return (duid) => this._duidsToConnectorAuths.get(duid);
  },
  getConnectorAuthsForConnector() {
    return (connectorDuid) => this.connectorAuthList.filter((auth) => auth.connectorDuid === connectorDuid);
  },
};

const actions: PiniaActionAdaptor<Actions, DataStore> = {
  async createConnector(name, url, partialConnector = {}, options = {}) {
    const { awaitBackend = false } = options;
    const connector: Connector = {
      duid: makeDuid(),
      name,
      url,
      iconUrl: null,
      enabled: partialConnector.enabled ?? true,
      authRequired: true,
      clientId: partialConnector.clientId ?? "",
      hasClientSecret: false,
    };

    const create: ConnectorCreate = {
      duid: connector.duid,
      name: connector.name,
      url: connector.url,
      enabled: connector.enabled,
      clientId: connector.clientId,
      ...(partialConnector.clientSecret ? { clientSecret: partialConnector.clientSecret } : {}),
    };
    const backendAction = this.$backend.connector.create(create);
    if (awaitBackend) {
      const result = await backendAction;
      const createdConnector = result.models.connectors?.find(
        (backendConnector) => backendConnector.duid === connector.duid
      );
      if (createdConnector) {
        this.$createOrUpdateConnector(createdConnector);
        return createdConnector;
      }
    }
    this.$createOrUpdateConnector(connector);
    return connector;
  },
  async updateConnector(update, options = {}) {
    const { awaitBackend = false, noBackend = false } = options;
    const connector = this.getConnectorByDuid(update.duid);
    if (!connector) {
      return undefined;
    }

    const localUpdate = { ...update };
    delete localUpdate.clientSecret;
    if (localUpdate.url && localUpdate.url !== connector.url) {
      connector.iconUrl = null;
    }
    Object.assign(connector, localUpdate);
    if (noBackend) {
      return connector;
    }

    const backendAction = this.$backend.connector.update(update);
    if (awaitBackend) {
      const result = await backendAction;
      const updatedConnector = result.models.connectors?.find(
        (backendConnector) => backendConnector.duid === connector.duid
      );
      if (updatedConnector) {
        this.$createOrUpdateConnector(updatedConnector);
        return updatedConnector;
      }
    }
    return connector;
  },
  async deleteConnector(connector, options = {}) {
    const { awaitBackend = false } = options;

    this.$deleteConnector(connector);

    const backendAction = this.$backend.connector.delete(connector.duid);
    if (awaitBackend) {
      await backendAction;
    }
  },
  async updateConnectorAuth(update, options = {}) {
    const { awaitBackend = false, noBackend = false } = options;
    const connectorAuth = this.getConnectorAuthByDuid(update.duid);
    if (!connectorAuth) {
      return undefined;
    }

    Object.assign(connectorAuth, update);
    if (noBackend) {
      return connectorAuth;
    }

    const backendAction = this.$backend.connectorAuth.update(update);
    if (awaitBackend) {
      const result = await backendAction;
      const updatedConnectorAuth = result.models.connectorAuths?.find(
        (backendConnectorAuth) => backendConnectorAuth.duid === connectorAuth.duid
      );
      if (updatedConnectorAuth) {
        this.$createOrUpdateConnectorAuth(updatedConnectorAuth);
        return updatedConnectorAuth;
      }
    }
    return connectorAuth;
  },
  async deleteConnectorAuth(connectorAuth, options = {}) {
    const { awaitBackend = false } = options;

    this.$deleteConnectorAuth(connectorAuth);

    const backendAction = this.$backend.connectorAuth.delete(connectorAuth.duid);
    if (awaitBackend) {
      await backendAction;
    }
  },
  $setConnectors(connectors) {
    this._duidsToConnectors = new Map(connectors.map((connector) => [connector.duid, connector]));
  },
  $setConnectorAuths(connectorAuths) {
    this._duidsToConnectorAuths = new Map(connectorAuths.map((connectorAuth) => [connectorAuth.duid, connectorAuth]));
  },
  $createOrUpdateConnector(connector) {
    const existing = this._duidsToConnectors.get(connector.duid);
    if (existing) {
      Object.assign(existing, connector);
    } else {
      this._duidsToConnectors.set(connector.duid, connector);
    }
  },
  $deleteConnector(connector) {
    this._duidsToConnectors.delete(connector.duid);
    this.connectorAuthList
      .filter((auth) => auth.connectorDuid === connector.duid)
      .forEach((auth) => this.$deleteConnectorAuth(auth));
  },
  $createOrUpdateConnectorAuth(connectorAuth) {
    const existing = this._duidsToConnectorAuths.get(connectorAuth.duid);
    if (existing) {
      Object.assign(existing, connectorAuth);
    } else {
      this._duidsToConnectorAuths.set(connectorAuth.duid, connectorAuth);
    }
  },
  $deleteConnectorAuth(connectorAuth) {
    this._duidsToConnectorAuths.delete(connectorAuth.duid);
  },
};

export { actions, getters };
